import { DatePeriodInTimestamp } from '@cast/design-system';
import {
  OOMKillEventDetails,
  RecommendedPodCountChangedEventDetails,
  RecommendedRequestsChangedEventDetails,
  ScalingPolicyAssignedEventDetails,
  ScalingPolicyCreatedEventDetails,
  ScalingPolicyUpdatedEventDetails,
  ScalingPolicyDeletedEventDetails,
  ScalingPolicyOrderUpdatedEventDetails,
  SurgeEventDetails,
  WorkloadConfigChangedEventDetails,
  WorkloadEventType,
  WorkloadOptimizationEvent,
  FailedHelmTestEventDetails,
  WorkloadOptimizationMetricSpec,
  SystemOverrideResetEventDetails,
  SystemOverrideTriggeredEventDetails,
  StartupFailureEventDetails,
  CPUPressureEventDetails,
  CPUPressureResolvedEventDetails,
} from '@cast/types';

import { CAST_EVENTS, ERROR_EVENTS } from './_constants';

type BaseEvent = {
  timestamp: string;
  displayName: string;
  displayNameShort: string;
  containerNames: string[];
  color: string;
} & Omit<WorkloadOptimizationEvent, 'type' | 'event'>;

type _PreparedEvent<T extends WorkloadEventType, D> = BaseEvent & {
  type: T;
  details: D;
};

export type SurgeEvent = _PreparedEvent<'EVENT_TYPE_SURGE', SurgeEventDetails>;

export type OOMKillEvent = _PreparedEvent<
  'EVENT_TYPE_OOM_KILL',
  OOMKillEventDetails
>;

export type RecommendedPodCountChangedEvent = _PreparedEvent<
  'EVENT_TYPE_RECOMMENDED_POD_COUNT_CHANGED',
  RecommendedPodCountChangedEventDetails
>;

export type RecommendedRequestsChangedEvent = _PreparedEvent<
  'EVENT_TYPE_RECOMMENDED_REQUESTS_CHANGED',
  RecommendedRequestsChangedEventDetails
>;

export type ConfigChangedEventV2 = _PreparedEvent<
  'EVENT_TYPE_CONFIGURATION_CHANGEDV2',
  WorkloadConfigChangedEventDetails
>;

type ScalingPolicyAssignedEvent = _PreparedEvent<
  'EVENT_TYPE_SCALING_POLICY_ASSIGNED',
  ScalingPolicyAssignedEventDetails
>;

type ScalingPolicyCreatedEvent = _PreparedEvent<
  'EVENT_TYPE_SCALING_POLICY_CREATED',
  ScalingPolicyCreatedEventDetails
>;

export type ScalingPolicyUpdatedEvent = _PreparedEvent<
  'EVENT_TYPE_SCALING_POLICY_UPDATED',
  ScalingPolicyUpdatedEventDetails
>;

type ScalingPolicyDeletedEvent = _PreparedEvent<
  'EVENT_TYPE_SCALING_POLICY_DELETED',
  ScalingPolicyDeletedEventDetails
>;

export type ScalingPolicyOrderChangeEvent = _PreparedEvent<
  'EVENT_TYPE_SCALING_POLICY_ORDER_UPDATED',
  ScalingPolicyOrderUpdatedEventDetails
>;

export type FailedHelmTestEvent = _PreparedEvent<
  'EVENT_TYPE_FAILED_HELM_TEST_HOOK',
  FailedHelmTestEventDetails
>;

export type SystemOverrideResetEvent = _PreparedEvent<
  'EVENT_TYPE_SYSTEM_OVERRIDE_RESET',
  SystemOverrideResetEventDetails
>;

export type SystemOverrideTriggeredEvent = _PreparedEvent<
  'EVENT_TYPE_SYSTEM_OVERRIDE_TRIGGERED',
  SystemOverrideTriggeredEventDetails
>;

export type StartupFailureEvent = _PreparedEvent<
  'EVENT_TYPE_STARTUP_FAILURE',
  StartupFailureEventDetails
>;

export type CPUPressureEvent = _PreparedEvent<
  'EVENT_TYPE_CPU_PRESSURE',
  CPUPressureEventDetails
>;

export type CPUPressureResolvedEvent = _PreparedEvent<
  'EVENT_TYPE_CPU_PRESSURE_RESOLVED',
  CPUPressureResolvedEventDetails
>;

type UnknownEvent = _PreparedEvent<WorkloadEventType, unknown>;

export type ScalingPolicyEvent =
  | ScalingPolicyAssignedEvent
  | ScalingPolicyCreatedEvent
  | ScalingPolicyUpdatedEvent
  | ScalingPolicyDeletedEvent;

export type PreparedEvent =
  | SurgeEvent
  | OOMKillEvent
  | FailedHelmTestEvent
  | ConfigChangedEventV2
  | RecommendedPodCountChangedEvent
  | RecommendedRequestsChangedEvent
  | ScalingPolicyEvent
  | ScalingPolicyOrderChangeEvent
  | SystemOverrideResetEvent
  | SystemOverrideTriggeredEvent
  | StartupFailureEvent
  | CPUPressureEvent
  | CPUPressureResolvedEvent
  | UnknownEvent;

export const SCALING_POLICY_EVENTS: WorkloadEventType[] = [
  WorkloadEventType.SCALING_POLICY_ASSIGNED,
  WorkloadEventType.SCALING_POLICY_CREATED,
  WorkloadEventType.SCALING_POLICY_UPDATED,
  WorkloadEventType.SCALING_POLICY_DELETED,
];

export const isScalingPolicyEvent = (
  event: PreparedEvent
): event is ScalingPolicyEvent => {
  return SCALING_POLICY_EVENTS.includes(event.type);
};

export const isScalingPolicyOrderChangeEvent = (
  event: PreparedEvent
): event is ScalingPolicyOrderChangeEvent => {
  return event.type === WorkloadEventType.SCALING_POLICY_ORDER_UPDATED;
};

type ErrorEvent = OOMKillEvent | FailedHelmTestEvent | StartupFailureEvent;

type CastEvent = SystemOverrideResetEvent | SystemOverrideTriggeredEvent;

// Type guards
export const isErrorEvent = (event: PreparedEvent): event is ErrorEvent => {
  return ERROR_EVENTS.includes(event.type);
};

export const isCastEvent = (event: PreparedEvent): event is CastEvent => {
  return CAST_EVENTS.includes(event.type);
};

export const isConfigChangedEvent = (
  event: PreparedEvent
): event is ConfigChangedEventV2 => {
  return event.type === WorkloadEventType.CONFIGURATION_CHANGEDV2;
};

export const isRecommendedRequestsChangedEvent = (
  event: PreparedEvent
): event is RecommendedRequestsChangedEvent => {
  return event.type === WorkloadEventType.RECOMMENDED_REQUESTS_CHANGED;
};

export type WorkloadQueryRangeSelection = {
  periodLabel?: string;
  timezone?: string;
  range?: DatePeriodInTimestamp;
};

export type WorkloadOptimizationBasicBehaviorFormModel = {
  minReplicas: number;
  maxReplicas: number;
};

// todo:task move to sibling _types
export type WorkloadOptimizationMetricFormModel = {
  cpu: number | null;
  memory: number | null;
};

export type WorkloadOptimizationScalingPolicyFormModel = {
  value: number | null;
  periodSeconds: number | null;
} | null;

// todo:task why stabilizationWindowSeconds is nullable
// todo:task check all properties
export type WorkloadOptimizationAutoscalerBehaviorFormModel = {
  scaleUp: {
    stabilizationWindowSeconds: number | null;
    pods: WorkloadOptimizationScalingPolicyFormModel;
    percent: WorkloadOptimizationScalingPolicyFormModel;
  };
  scaleDown: {
    stabilizationWindowSeconds: number | null;
    pods: WorkloadOptimizationScalingPolicyFormModel;
    percent: WorkloadOptimizationScalingPolicyFormModel;
  };
};

// todo:task move to sibling _types
export type ResourceMetric = Pick<
  Required<WorkloadOptimizationMetricSpec>,
  'type' | 'resource'
>;
